import React, { useContext, useEffect, useState, useRef } from "react";
import { formatTimeNumerical } from "../../Helpers/helpers";
import { GameContext } from "../../Context/GameContext";

/** Component props */
type TimerProps = {
  /** Callback to bubble remaining seconds up to parent */
  updateRemainingTime: (seconds: number) => void;
  /** If true, temporarily stops the countdown (default: false) */
  paused?: boolean;
};

/** Minimal shape we read from GameContext (adjust if your context exports types) */
type ProfileSettings = {
  escapeRoomTime: number; // total allowed time in seconds
};

type GameContextSlice = {
  startTime: number; // unix seconds when the game started
  isWinnerAccessible: boolean;
  setIsTimeUp: (isUp: boolean) => void;
  profileSettings: ProfileSettings;
};

/**
 * Timer
 * -----
 * - Counts down from `profileSettings.escapeRoomTime` based on `startTime`.
 * - Emits remaining seconds via `updateRemainingTime`.
 * - Calls `setIsTimeUp(true)` when time hits zero.
 * - Pauses when `paused` is true or when `isWinnerAccessible` is true.
 */
const Timer: React.FC<TimerProps> = ({ updateRemainingTime, paused = false }) => {
  const {
    startTime,
    isWinnerAccessible,
    setIsTimeUp,
    profileSettings,
  } = useContext(GameContext) as GameContextSlice;

  const [remainingTime, setRemainingTime] = useState<number>(
    profileSettings?.escapeRoomTime ?? 1200
  );

  // Keep a handle to the running interval so we can clear it reliably
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Always clear on re-run to avoid multiple intervals
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Guard conditions: nothing to do if we haven't started, are paused, or the win screen is open
    if (startTime === 0 || isWinnerAccessible || paused || !profileSettings) {
      return;
    }

    // Tick every 500ms for a responsive UI
    intervalRef.current = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const elapsed = now - startTime;
      const total = profileSettings.escapeRoomTime;
      const nextRemaining = total - elapsed;

      if (nextRemaining > 0) {
        setRemainingTime(nextRemaining);
        updateRemainingTime(nextRemaining);
      } else {
        // Time is up: clamp to zero, notify, and stop ticking
        setRemainingTime(0);
        updateRemainingTime(0);
        setIsTimeUp(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }, 500);

    // Cleanup when deps change or component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    startTime,
    isWinnerAccessible,
    paused,
    profileSettings?.escapeRoomTime,
    setIsTimeUp,
    updateRemainingTime,
  ]);

  return (
    <div className="timer">
      <p>{formatTimeNumerical(remainingTime)}</p>
    </div>
  );
};

export default Timer;
